import { useState, createContext } from 'react'
import differenceInHours from 'date-fns/differenceInHours'
import { now } from 'utils/dateUtils'

export const BlissContext = createContext()

const BlissContextContainer = ({ children }) => {
  const [blissQuote, setBlissQuote] = useState(new Map())

  const addChargeId = (reservation_uuid, reservation_charge_id) => {
    setBlissQuote(oldVal => {
      const newVal = new Map(oldVal)
      newVal.set(reservation_uuid, { ...oldVal.get(reservation_uuid), reservation_charge_id })
      return newVal
    })
  }

  const removeChargeIds = () => {
    setBlissQuote(oldVal => {
      const newVal = new Map(oldVal)
      for (const key of newVal.keys()) {
        const { reservation_charge_id, ...cleanedQuote } = newVal.get(key)
        newVal.set(key, cleanedQuote)
      }
      return newVal
    })
  }

  const getToken = async() => {
    const tokenResponse = await fetch(process.env.GATSBY_BLISS_ACCESS_TOKEN_ENDPOINT)
    const formattedResponse = await tokenResponse.json()
    process.env.LOG_ENDPOINTS && console.log('bliss endpoint: token, response:', formattedResponse)
    if (tokenResponse.ok) {
      if (formattedResponse.access_token) {
        return formattedResponse.access_token
      }
    }
  }

  const getQuote = async(reservation) => {
    // Don't allow trip guarantees if the reservation is less than 48 hours away
    const checkinTime = new Date(`${reservation.checkin}T23:00:00Z`)
    if (differenceInHours(checkinTime, now()) < 48) {
      return {}
    }

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: 'quote',
        token: await getToken(),
        body: {
          product_id: process.env.GATSBY_BLISS_PRODUCT_ID,
          product_price: +reservation.grand_total,
          eventStartTime: `${reservation.checkin}T23:00:00Z`,
          eventEndTime: `${reservation.checkout}T19:00:00Z`,
          data: reservation
        }
      })
    }
    const response = await fetch(process.env.GATSBY_BLISS_QUOTE_ENDPOINT, options)
    const formattedResponse = await response.json()
    if (response.ok) {
      setBlissQuote(oldVal => {
        const newVal = new Map(oldVal)
        newVal.set(reservation.uuid, formattedResponse)
        return newVal
      })
    }
    process.env.LOG_ENDPOINTS && console.log('bliss endpoint: quote, response:', formattedResponse)
    return formattedResponse
  }

  const getQuotes = async(folio) => {
    if (!folio) { return }

    // clear stale quotes
    const validReservationIds = folio.reservations.map(reservation => reservation.uuid)
    for (const quoteKey of blissQuote.keys()) {
      if (!validReservationIds.includes(quoteKey)) {
        blissQuote.delete(quoteKey)
      }
    }

    for (const reservation of folio.reservations) {
      if (!blissQuote.has(reservation.uuid)) {
        await getQuote(reservation)
      }
    }
  }

  const acceptQuote = async(quoteKey, userDetails) => {
    const quote = blissQuote.get(quoteKey)
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: 'quote/accept',
        token: await getToken(),
        body: {
          quote_id: quote.quote_id,
          reservation_id: quote.data.uuid,
          user: {
            name: `${userDetails.first_name} ${userDetails.last_name}`,
            email: userDetails.email
          }
        }
      })
    }
    const response = await fetch(process.env.GATSBY_BLISS_QUOTE_ENDPOINT, options)
    const formattedResponse = await response.json()
    setBlissQuote(oldVal => {
      const newVal = new Map(oldVal)
      newVal.delete(quoteKey)
      return newVal
    })
    process.env.LOG_ENDPOINTS && console.log('bliss endpoint: quote/accept, response:', formattedResponse)
  }

  const acceptAllQuotes = async(userDetails) => {
    if (userDetails?.email) {
      for (const quoteKey of blissQuote.keys()) {
        await acceptQuote(quoteKey, userDetails)
      }
    }
  }

  const declineQuote = async(quoteKey) => {
    const quote = blissQuote.get(quoteKey)
    if (!quote?.quote_id) { return }
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: 'quote/decline',
        token: await getToken(),
        body: {
          quote_id: quote.quote_id
        }
      })
    }
    const response = await fetch(process.env.GATSBY_BLISS_QUOTE_ENDPOINT, options)
    const formattedResponse = await response.json()
    setBlissQuote(oldVal => {
      const newVal = new Map(oldVal)
      newVal.delete(quoteKey)
      return newVal
    })
    process.env.LOG_ENDPOINTS && console.log('bliss endpoint: quote/decline, response:', formattedResponse)
  }

  const declineAllQuotes = async() => {
    for (const quoteKey of blissQuote.keys()) {
      await declineQuote(quoteKey)
    }
  }

  const cancelGuarantee = async(quoteId) => {
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: 'guarantee/cancel',
        token: await getToken(),
        body: {
          quote_id: quoteId
        }
      })
    }
    const response = await fetch(process.env.GATSBY_BLISS_QUOTE_ENDPOINT, options)
    const formattedResponse = await response.json()
    process.env.LOG_ENDPOINTS && console.log('bliss endpoint: guarantee/cancel, response:', formattedResponse)
  }

  return (
    <BlissContext.Provider value={{
      addChargeId,
      removeChargeIds,
      getQuote,
      getQuotes,
      acceptAllQuotes,
      declineQuote,
      declineAllQuotes,
      cancelGuarantee,
      blissQuote: Array.from(blissQuote.values())
    }}
    >
      {children}
    </BlissContext.Provider>
  )
}

export default BlissContextContainer